// Entregas e a situação de cada caminhão (0.19.0; pedidos do titular em
// 11/10/2026): a carga entregue, as paradas no caminho (entregas
// fracionadas), o complemento e o caminhão vazio. Contas puras.
//
// Caminhão vazio: sem carga no caminhão (carregada até hoje e ainda sem
// entregar), mesmo no meio da viagem. O lugar é a cidade da última entrega
// (sem GPS); "desde" é a hora do botão "Carga entregue" (ou o dia da descarga).
import { FUSO_PADRAO } from './datas.js';
import { data } from './formato.js';

const vivo = (x) => !x.deleted_at;
export const lugar = (cidade, uf) => [cidade, uf].filter(Boolean).join('/');

// As paradas de uma carga, na ordem.
export function paradasDaCarga(entregas, freteId) {
  return (entregas ?? [])
    .filter((e) => vivo(e) && e.frete_id === freteId)
    .sort((a, b) => a.ordem - b.ordem || String(a.created_at ?? '').localeCompare(String(b.created_at ?? '')));
}

// A carga está no caminhão: carregada até hoje e ainda sem entregar.
export const cargaNoCaminhao = (f, hoje) => vivo(f) && f.data_carregamento <= hoje && !f.data_descarga;

// A próxima parada: a primeira parada sem entregar ou o destino final.
export function proximaParada(f, paradas) {
  const pendente = paradas.find((p) => !p.data_entrega);
  if (pendente) return { tipo: 'parada', parada: pendente, cidade: pendente.cidade, uf: pendente.uf };
  return { tipo: 'destino', cidade: f.destino_cidade ?? null, uf: f.destino_uf ?? null };
}

// "Curitiba/PR → Joinville/SC → Laguna/SC"
export function textoDaRota(f, paradas = []) {
  return [lugar(f.origem_cidade, f.origem_uf), ...paradas.map((p) => lugar(p.cidade, p.uf)), lugar(f.destino_cidade, f.destino_uf)]
    .filter(Boolean)
    .join(' → ');
}

// As cargas da viagem na ordem da tela: cada principal seguida dos complementos dela.
export function cargasEmOrdem(cargas) {
  const ids = new Set(cargas.map((f) => f.id));
  const principais = cargas.filter((f) => !f.complemento_de || !ids.has(f.complemento_de));
  return principais.flatMap((p) => [p, ...cargas.filter((f) => f.complemento_de === p.id)]);
}

// As cargas que podem receber complemento: as da viagem que não são complemento.
export function principaisDaViagem(cargas, { menos = null } = {}) {
  return cargas.filter((f) => vivo(f) && !f.complemento_de && f.id !== menos);
}

// Para ordenar as entregas: o dia e, no mesmo dia, a hora do botão.
const momentoDaEntrega = (f) => `${f.data_descarga}|${f.entregue_em ?? ''}`;

// A situação de cada caminhão ativo:
//   { veiculo, viagem (a em andamento ou null), estado: 'carregado' | 'vazio',
//     carregado: cargas (no caminhão), carga (a principal), proxima ({ cidade, uf, tipo })
//     vazio: lugar ({ cidade, uf } da última entrega, ou null), desde (entregue_em ou o dia) }
export function situacaoDosCaminhoes({ veiculos = [], viagens = [], fretes = [], entregas = [] }, { hoje }) {
  const tratores = veiculos.filter((v) => vivo(v) && v.tipo === 'trator' && v.ativo !== false);
  const viagensVivas = viagens.filter((v) => vivo(v) && v.status !== 'cancelada');
  const cargasVivas = fretes.filter(vivo);
  return tratores.map((veiculo) => {
    const dele = viagensVivas.filter((v) => v.veiculo_id === veiculo.id);
    const viagem = dele.filter((v) => v.status === 'aberta').sort((a, b) => String(b.data_saida).localeCompare(String(a.data_saida)))[0] ?? null;
    const ids = new Set(dele.map((v) => v.id));
    const cargasDele = cargasVivas.filter((f) => ids.has(f.viagem_id));
    const noCaminhao = viagem
      ? cargasDele.filter((f) => f.viagem_id === viagem.id && cargaNoCaminhao(f, hoje))
        .sort((a, b) => String(a.data_carregamento).localeCompare(String(b.data_carregamento)))
      : [];
    if (noCaminhao.length > 0) {
      const carga = noCaminhao.find((f) => !f.complemento_de) ?? noCaminhao[0];
      return { veiculo, viagem, estado: 'carregado', cargas: noCaminhao, carga, proxima: proximaParada(carga, paradasDaCarga(entregas, carga.id)) };
    }
    const ultima = cargasDele.filter((f) => f.data_descarga && f.data_descarga <= hoje)
      .sort((a, b) => momentoDaEntrega(b).localeCompare(momentoDaEntrega(a)))[0] ?? null;
    return {
      veiculo,
      viagem,
      estado: 'vazio',
      lugar: ultima && (ultima.destino_cidade || ultima.destino_uf) ? { cidade: ultima.destino_cidade ?? null, uf: ultima.destino_uf ?? null } : null,
      desde: ultima ? (ultima.entregue_em ?? ultima.data_descarga) : null,
    };
  });
}

// "11/10, 14h" (ou "11/10, 14h30"; só o dia quando não tem a hora).
export function textoDoMomento(quando, { fuso = FUSO_PADRAO } = {}) {
  if (!quando) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(quando)) return data(quando).slice(0, 5);
  const partes = new Intl.DateTimeFormat('pt-BR', {
    timeZone: fuso || FUSO_PADRAO, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(quando));
  const valor = (tipo) => partes.find((p) => p.type === tipo)?.value ?? '';
  const minutos = valor('minute');
  return `${valor('day')}/${valor('month')}, ${Number(valor('hour'))}h${minutos === '00' ? '' : minutos}`;
}

// A situação em uma linha: "Carregado: Curitiba/PR → Laguna/SC" ou
// "Vazio em Laguna/SC desde 11/10, 14h".
export function textoDaSituacao(s, { fuso } = {}) {
  if (s.estado === 'carregado') {
    const rota = textoDaRota(s.carga);
    const parada = s.proxima.tipo === 'parada' ? ` (próxima entrega: ${lugar(s.proxima.cidade, s.proxima.uf)})` : '';
    return `Carregado${rota ? `: ${rota}` : ''}${parada}`;
  }
  const onde = s.lugar ? ` em ${lugar(s.lugar.cidade, s.lugar.uf)}` : '';
  const desde = s.desde ? ` desde ${textoDoMomento(s.desde, { fuso })}` : '';
  return `Vazio${onde}${desde}${s.viagem ? '' : ' · sem viagem em andamento'}`;
}
